import React from "react";
import { motion } from "framer-motion";
import { ClipboardCheck, Send, Repeat, Sparkles } from "lucide-react";

const STEPS = [
  {
    n: "01",
    icon: ClipboardCheck,
    title: "Customer drops off",
    body: "Staff log the order in 30 seconds. Name, phone number, job details.",
  },
  {
    n: "02",
    icon: Send,
    title: "Job Complete — Notify Instantly",
    body: "One tap marks the job as ready. Ping's AI automatically sends a personalised WhatsApp message to your customer. No phone calls, no delays.",
  },
  {
    n: "03",
    icon: Repeat,
    title: "AI-Powered Marketing Campaigns",
    body: "Run seasonal and targeted campaigns with one click. Ping's AI crafts the message, picks the audience, and sends to hundreds of customers for pennies — turning one-time visitors into loyal regulars.",
  },
  {
    n: "04",
    icon: Sparkles,
    title: "Grow With Intelligence",
    body: "Ping learns from your customer interactions to surface insights, suggest the best time to reach out, and help your business grow on autopilot.",
  },
];

export const HowItWorks = () => {
  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="relative bg-[#1A1A2E] text-white py-20 sm:py-28 lg:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid-dark opacity-50 pointer-events-none" />
      <div className="orb w-[460px] h-[460px] bg-[#4B9FE8] -top-32 -left-20" />
      <div className="orb w-[460px] h-[460px] bg-[#7B5EA7] -bottom-32 -right-20" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] ping-gradient-text mb-4">
            How it works
          </p>
          <h2 className="text-4xl sm:text-5xl lg:text-[3.5rem] lg:leading-[1.05] font-black tracking-tight">
            Up and running in <span className="ping-gradient-text">10 minutes</span>
          </h2>
          <p className="mt-5 text-lg text-white/65 max-w-2xl">
            No technical setup. No IT team. Just open Ping on any device and
            start — with AI working behind the scenes from day one.
          </p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-7">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.n}
                data-testid={`how-it-works-step-${i + 1}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.55, delay: i * 0.1 }}
                className="relative group"
              >
                {/* Connector line on desktop */}
                {i < STEPS.length - 1 && (
                  <div className="hidden lg:block absolute top-7 left-[calc(100%-12px)] w-full h-px bg-gradient-to-r from-white/20 to-transparent z-0" />
                )}

                <div className="relative bg-white/[0.04] backdrop-blur border border-white/10 rounded-2xl p-6 lg:p-7 h-full hover:bg-white/[0.07] hover:border-white/20 transition-all duration-300">
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-14 h-14 rounded-full ping-gradient flex items-center justify-center font-black text-white text-lg shadow-lg shadow-[#7B5EA7]/30">
                      {s.n}
                    </div>
                    <Icon size={22} className="text-white/40" strokeWidth={1.8} />
                  </div>
                  <h3 className="text-xl font-bold tracking-tight mb-2">
                    {s.title}
                  </h3>
                  <p className="text-white/65 text-[15px] leading-relaxed">
                    {s.body}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
